import { useState, useEffect, useCallback, useRef } from 'react';
import { api, ApiError } from '../api/client';
import './LicenseAssignment.css';

/* ─── Types ──────────────────────────────────────────────── */

type RunStatus = 'queued' | 'running' | 'completed' | 'failed';
type ItemStatus = 'pending' | 'success' | 'skipped' | 'partial' | 'error';

interface LicenseRun {
  id: string;
  status: RunStatus;
  panels_total: number;
  panels_success: number;
  panels_skipped: number;
  panels_partial: number;
  panels_error: number;
  licenses_assigned: number;
  licenses_already: number;
  licenses_failed: number;
  triggered_by: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

interface LicenseRunItem {
  id: string;
  run_id: string;
  panel_id: string;
  panel_reseller_id: string | null;
  panel_email: string;
  status: ItemStatus;
  total_users: number;
  assigned: number;
  already_licensed: number;
  failed: number;
  errors: string[];
  note: string | null;
  started_at: string | null;
  finished_at: string | null;
}

interface RunDetail {
  run: LicenseRun;
  items: LicenseRunItem[];
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

/* ─── Helpers ────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function durationLabel(start: string | null, end: string | null): string {
  if (!start) return '—';
  const endMs = end ? new Date(end).getTime() : Date.now();
  const ms = endMs - new Date(start).getTime();
  if (ms < 1000) return '<1s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const restSec = sec % 60;
  return restSec === 0 ? `${min}m` : `${min}m ${restSec}s`;
}

const ChevronIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const PlayIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const RefreshIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

/* ─── Main component ─────────────────────────────────────── */

const INITIAL_VISIBLE = 15;
const PAGE_INCREMENT = 40;
const SERVER_PAGE = 50;

export function LicenseAssignment(): JSX.Element {
  const [runs, setRuns] = useState<LicenseRun[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [starting, setStarting] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderExhausted, setOlderExhausted] = useState(false);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  /* ─── List view fetch + polling ──────────────────────── */

  // Polling refreshes the head (latest 50). Any older runs already paginated
  // into state are preserved by id-merging — historical rows are immutable.
  const refreshList = useCallback(async (): Promise<void> => {
    try {
      const data = await api<LicenseRun[]>('/api/license-runs');
      setRuns((prev) => {
        if (!prev) return data;
        const headIds = new Set(data.map((r) => r.id));
        const older = prev.filter((r) => !headIds.has(r.id));
        return [...data, ...older];
      });
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load runs');
    }
  }, [showToast]);

  const loadOlder = useCallback(async (): Promise<void> => {
    if (!runs || runs.length === 0 || loadingOlder || olderExhausted) return;
    setLoadingOlder(true);
    try {
      const oldest = runs[runs.length - 1];
      const data = await api<LicenseRun[]>(
        `/api/license-runs?before=${encodeURIComponent(oldest.created_at)}&limit=${SERVER_PAGE}`,
      );
      if (data.length === 0) {
        setOlderExhausted(true);
      } else {
        setRuns((prev) => [...(prev ?? []), ...data]);
        setVisibleCount((c) => c + PAGE_INCREMENT);
      }
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load older runs');
    } finally {
      setLoadingOlder(false);
    }
  }, [runs, loadingOlder, olderExhausted, showToast]);

  const showMore = useCallback((): void => {
    if (!runs) return;
    if (visibleCount < runs.length) {
      setVisibleCount((c) => Math.min(c + PAGE_INCREMENT, runs.length));
    } else {
      void loadOlder();
    }
  }, [runs, visibleCount, loadOlder]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  // Two cadences on the list view:
  //   - Fast poll (3s) while any run is in-flight
  //   - Slow poll (7s) when nothing is in-flight, so a teammate's just-started
  //     run shows up here within seconds without a manual refresh.
  const listPollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedId !== null) return; // detail view has its own polling
    const hasInflight = runs?.some((r) => r.status === 'queued' || r.status === 'running');
    const intervalMs = hasInflight ? 3000 : 7000;
    if (listPollIntervalRef.current !== null) {
      window.clearInterval(listPollIntervalRef.current);
    }
    listPollIntervalRef.current = window.setInterval(() => {
      void refreshList();
    }, intervalMs);
    return () => {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
    };
  }, [runs, selectedId, refreshList]);

  /* ─── Detail view fetch + polling ────────────────────── */

  const refreshDetail = useCallback(async (id: string): Promise<void> => {
    try {
      const data = await api<RunDetail>(`/api/license-runs/${id}`);
      setDetail(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load run');
    }
  }, [showToast]);

  const detailPollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      if (detailPollIntervalRef.current !== null) {
        window.clearInterval(detailPollIntervalRef.current);
        detailPollIntervalRef.current = null;
      }
      return;
    }
    void refreshDetail(selectedId);
    detailPollIntervalRef.current = window.setInterval(() => {
      void refreshDetail(selectedId);
    }, 2500);
    return () => {
      if (detailPollIntervalRef.current !== null) {
        window.clearInterval(detailPollIntervalRef.current);
        detailPollIntervalRef.current = null;
      }
    };
  }, [selectedId, refreshDetail]);

  // Stop detail polling when run is terminal
  useEffect(() => {
    if (!detail) return;
    if (detail.run.status === 'completed' || detail.run.status === 'failed') {
      if (detailPollIntervalRef.current !== null) {
        window.clearInterval(detailPollIntervalRef.current);
        detailPollIntervalRef.current = null;
      }
    }
  }, [detail]);

  /* ─── Start a run ────────────────────────────────────── */

  const startRun = async (): Promise<void> => {
    setStarting(true);
    try {
      const result = await api<{ runId: string; panelsQueued: number }>('/api/license-runs', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      showToast('success', `Started — ${result.panelsQueued} panels queued`);
      await refreshList();
      setSelectedId(result.runId);
    } catch (err) {
      // Singleton lock: another run is already in progress. Jump to it instead
      // of showing an error — that's the intended UX when many people share /team.
      if (err instanceof ApiError && err.code === 'RUN_IN_PROGRESS') {
        const existingRunId = (err.data as { existingRunId?: string } | undefined)?.existingRunId;
        if (existingRunId) {
          showToast('success', 'A run is already in progress — opening it now.');
          await refreshList();
          setSelectedId(existingRunId);
          return;
        }
      }
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to start run';
      showToast('error', msg);
    } finally {
      setStarting(false);
    }
  };

  /* ─── Render ─────────────────────────────────────────── */

  if (selectedId !== null) {
    return (
      <div className="lic-root">
        <div className="lic-scroll">
          <header className="lic-header">
            <button
              type="button"
              className="lic-back"
              onClick={() => setSelectedId(null)}
            >
              <ChevronIcon />
              <span>Back to runs</span>
            </button>
          </header>
          {detail ? (
            <DetailView detail={detail} onRefresh={() => void refreshDetail(detail.run.id)} />
          ) : (
            <div className="lic-loading">Loading run…</div>
          )}
        </div>
        {toast && <div className={`lic-toast lic-toast--${toast.type}`}>{toast.message}</div>}
      </div>
    );
  }

  const totalRuns = runs?.length ?? 0;
  const visibleRuns = runs ? runs.slice(0, visibleCount) : null;
  const canShowMore = runs !== null && (visibleCount < runs.length || !olderExhausted);
  const inflight = runs?.find((r) => r.status === 'queued' || r.status === 'running') ?? null;

  return (
    <div className="lic-root">
      <div className="lic-scroll">
        <header className="lic-header">
          <div className="lic-header-row">
            <div>
              <h1 className="lic-title">License Assignment</h1>
              <p className="lic-subtitle">
                Run Workspace Business Starter license assignment across all admin panels.
                Idempotent — already-licensed users are skipped automatically.
              </p>
            </div>
            <button
              type="button"
              className="lic-btn lic-btn--primary"
              onClick={startRun}
              disabled={starting || inflight !== null}
              title={inflight ? 'A run is already in progress' : 'Run license check across all panels'}
            >
              <PlayIcon />
              {starting ? 'Starting…' : inflight ? 'Run in progress…' : 'Run License Check'}
            </button>
          </div>
        </header>

        <section className="lic-card">
          <div className="lic-card-header">
            <h2 className="lic-card-title">
              Runs{' '}
              <span className="lic-cell-faint">
                ({Math.min(visibleCount, totalRuns)}
                {totalRuns > 0 && <> of {totalRuns}{!olderExhausted && '+'}</>})
              </span>
            </h2>
            <button
              type="button"
              className="lic-icon-btn"
              onClick={() => void refreshList()}
              title="Refresh list"
            >
              <RefreshIcon />
            </button>
          </div>

          {runs === null && <div className="lic-empty">Loading…</div>}

          {runs !== null && runs.length === 0 && (
            <div className="lic-empty">
              <div className="lic-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </div>
              <h3 className="lic-empty-title">No runs yet</h3>
              <p className="lic-empty-text">
                Click <strong>Run License Check</strong> above to start the first run.
                It will iterate every panel in the database and assign licenses where missing.
              </p>
            </div>
          )}

          {visibleRuns !== null && visibleRuns.length > 0 && (
            <table className="lic-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Status</th>
                  <th className="lic-col-num">Panels</th>
                  <th className="lic-col-num">Assigned</th>
                  <th className="lic-col-num">Already</th>
                  <th>Triggered by</th>
                  <th className="lic-col-date">Started</th>
                  <th className="lic-col-date">Duration</th>
                </tr>
              </thead>
              <tbody>
                {visibleRuns.map((r) => (
                  <tr
                    key={r.id}
                    className="lic-row"
                    onClick={() => setSelectedId(r.id)}
                  >
                    <td className="lic-cell-mono lic-cell-faint">{r.id.slice(0, 8)}</td>
                    <td>
                      <span className={`lic-badge lic-badge--${r.status}`}>{r.status}</span>
                    </td>
                    <td className="lic-col-num">
                      <span className="lic-num lic-num--ink">{r.panels_total}</span>
                      {r.status === 'completed' && (r.panels_skipped + r.panels_partial + r.panels_error) > 0 && (
                        <span className="lic-num-detail">
                          {r.panels_skipped > 0 && <> · {r.panels_skipped} skip</>}
                          {(r.panels_partial + r.panels_error) > 0 && <> · {r.panels_partial + r.panels_error} fail</>}
                        </span>
                      )}
                    </td>
                    <td className="lic-col-num">
                      <span className="lic-num lic-num--sage">{r.licenses_assigned}</span>
                    </td>
                    <td className="lic-col-num">
                      <span className="lic-num lic-num--muted">{r.licenses_already}</span>
                    </td>
                    <td className="lic-cell-mono lic-cell-faint">{r.triggered_by ?? '—'}</td>
                    <td className="lic-col-date lic-cell-faint">{relativeTime(r.started_at ?? r.created_at)}</td>
                    <td className="lic-col-date lic-cell-faint">
                      {durationLabel(r.started_at, r.finished_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {visibleRuns !== null && visibleRuns.length > 0 && canShowMore && (
            <div className="lic-loadmore-row">
              <button
                type="button"
                className="lic-btn lic-btn--ghost"
                onClick={showMore}
                disabled={loadingOlder}
              >
                {loadingOlder ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
          {visibleRuns !== null && visibleRuns.length > 0 && !canShowMore && (
            <div className="lic-loadmore-row lic-cell-faint">All runs loaded.</div>
          )}
        </section>
      </div>
      {toast && <div className={`lic-toast lic-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── Detail view (per-panel results for one run) ────────── */

function DetailView({ detail, onRefresh }: { detail: RunDetail; onRefresh: () => void }): JSX.Element {
  const { run, items } = detail;
  const inflight = run.status === 'queued' || run.status === 'running';

  // Surface failures: error first, then partial, then everything else in
  // its existing order (Array.sort is stable in modern engines).
  const sortRank = (s: ItemStatus): number => (s === 'error' ? 0 : s === 'partial' ? 1 : 2);
  const sortedItems = [...items].sort((a, b) => sortRank(a.status) - sortRank(b.status));

  return (
    <>
      <section className="lic-summary-card">
        <div className="lic-summary-top">
          <div>
            <h2 className="lic-summary-title">
              Run <span className="lic-cell-mono lic-cell-faint">{run.id.slice(0, 8)}</span>
            </h2>
            <div className="lic-summary-meta">
              Triggered {run.triggered_by ? <>by <strong>{run.triggered_by}</strong></> : null} ·{' '}
              {relativeTime(run.created_at)}
              {run.started_at && (
                <> · ran {durationLabel(run.started_at, run.finished_at)}</>
              )}
            </div>
          </div>
          <div className="lic-summary-actions">
            <span className={`lic-badge lic-badge--${run.status}`}>{run.status}</span>
            <button
              type="button"
              className="lic-icon-btn"
              onClick={onRefresh}
              title="Refresh"
            >
              <RefreshIcon />
            </button>
          </div>
        </div>

        {run.error_message && (
          <div className="lic-summary-error">{run.error_message}</div>
        )}

        <div className="lic-summary-grid">
          <SummaryStat label="Panels" value={run.panels_total} hint="total" />
          <SummaryStat label="Success" value={run.panels_success} variant="sage" />
          <SummaryStat label="Skipped" value={run.panels_skipped} variant="honey" hint="no DWD" />
          <SummaryStat
            label="Failed"
            value={run.panels_partial + run.panels_error}
            variant={run.panels_partial + run.panels_error > 0 ? 'rose' : 'muted'}
            hint={run.panels_partial > 0 ? `${run.panels_partial} partial / ${run.panels_error} error` : undefined}
          />
          <SummaryStat label="Assigned" value={run.licenses_assigned} variant="sage" hint="new licenses" />
          <SummaryStat label="Already had" value={run.licenses_already} variant="muted" />
          <SummaryStat
            label="Failed users"
            value={run.licenses_failed}
            variant={run.licenses_failed > 0 ? 'rose' : 'muted'}
          />
        </div>
      </section>

      <section className="lic-card">
        <div className="lic-card-header">
          <h2 className="lic-card-title">
            Panel results ({items.length})
            {inflight && <span className="lic-inflight-pulse" />}
          </h2>
        </div>

        {items.length === 0 && <div className="lic-empty"><span>No panels in this run.</span></div>}

        {items.length > 0 && (
          <table className="lic-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Status</th>
                <th className="lic-col-num">Users</th>
                <th className="lic-col-num">Assigned</th>
                <th className="lic-col-num">Already</th>
                <th className="lic-col-num">Failed</th>
                <th>Note / errors</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => {
                const isBad = it.status === 'error' || it.status === 'partial';
                return (
                <tr
                  key={it.id}
                  className={isBad ? 'lic-row--bad' : undefined}
                >
                  <td className="lic-cell-mono">{it.panel_reseller_id ?? '—'}</td>
                  <td className="lic-cell-mono">{it.panel_email}</td>
                  <td>
                    <span className={`lic-badge lic-badge--${it.status}`}>{it.status}</span>
                  </td>
                  <td className="lic-col-num">{it.total_users}</td>
                  <td className="lic-col-num lic-num--sage">{it.assigned || '—'}</td>
                  <td className="lic-col-num lic-cell-faint">{it.already_licensed || '—'}</td>
                  <td className="lic-col-num lic-num--rose">{it.failed || '—'}</td>
                  <td className="lic-cell-faint lic-cell-detail">
                    {it.note && <span>{it.note}</span>}
                    {it.errors.length > 0 && (
                      <details className="lic-errors" open={isBad}>
                        <summary>{it.errors.length} error{it.errors.length === 1 ? '' : 's'}</summary>
                        <ul>
                          {it.errors.slice(0, 20).map((e, i) => (
                            <li key={i}>{e}</li>
                          ))}
                          {it.errors.length > 20 && <li>… and {it.errors.length - 20} more</li>}
                        </ul>
                      </details>
                    )}
                    {isBad && (
                      <div className="lic-cta-resolve">
                        Please resolve this issue immediately! If unsure how, please ping your teamlead.
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function SummaryStat({
  label,
  value,
  variant = 'default',
  hint,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted';
  hint?: string;
}): JSX.Element {
  return (
    <div className="lic-summary-stat">
      <span className="lic-summary-stat-label">{label}</span>
      <span className={`lic-summary-stat-value lic-summary-stat-value--${variant}`}>{value}</span>
      {hint && <span className="lic-summary-stat-hint">{hint}</span>}
    </div>
  );
}
